import { stringify } from "PFCore/helpers/use_deterministic_stringify";
import { NotificationSetting, ProfileNotificationSetting } from "PFTypes";
import { useCallback, useEffect, useMemo } from "react";
import { useForm, UseFormReturn } from "react-hook-form";

import { NotificationSettingsFormValues } from "./notifications_settings_form.types";
import { parseNotificationsSettingsToFormValues } from "./notifications_settings_form.utils";

type ChangedNotificationSetting = Pick<ProfileNotificationSetting, "id" | "frequency" | "important">;

type UseNotificationsSettingsFormProps = {
  notificationsSettings: ProfileNotificationSetting[] | NotificationSetting[];
  onSubmit: (changedSettings: ChangedNotificationSetting[]) => void;
};

type UseNotificationsSettingsFormReturn = {
  formMethods: UseFormReturn<NotificationSettingsFormValues>;
  isDirty: boolean;
  handleSubmit: () => void;
};

export const useNotificationsSettingsForm = ({
  notificationsSettings,
  onSubmit
}: UseNotificationsSettingsFormProps): UseNotificationsSettingsFormReturn => {
  const defaultValues = useMemo(
    () => parseNotificationsSettingsToFormValues(notificationsSettings),
    [notificationsSettings]
  );
  const formMethods = useForm<NotificationSettingsFormValues>({ defaultValues });
  const {
    handleSubmit,
    reset,
    formState: { isDirty }
  } = formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [stringify(defaultValues), reset]);

  const submitChangedValues = useCallback(
    (formData: NotificationSettingsFormValues) => {
      const payload: ChangedNotificationSetting[] = notificationsSettings
        .filter(
          (setting) =>
            formData[setting.id].frequency !== setting.frequency ||
            ((setting as ProfileNotificationSetting).important !== undefined &&
              formData[setting.id].important !== (setting as ProfileNotificationSetting).important)
        )
        .map(({ id }) => ({
          id,
          frequency: formData[id].frequency,
          important: formData[id].important
        }));
      onSubmit(payload);
    },
    [notificationsSettings, onSubmit]
  );

  const boundHandleSubmit = useCallback(handleSubmit(submitChangedValues), [
    handleSubmit,
    submitChangedValues
  ]);

  return {
    formMethods,
    isDirty,
    handleSubmit: boundHandleSubmit
  };
};
